import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Material } from "@/types/material/material.types";
import { InformationSource } from "@/types/information-source/information-source.types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { materialService } from "@/services/material/materialService";
import { useData } from "@/hooks/useData";
import { normalizeText } from "@/utils/textFormatter";

interface MaterialFormProps {
  isOpen: boolean;
  onClose: () => void;
  materialToEdit?: Material | null;
  onSuccess: () => void;
}

export const MaterialForm = ({
  isOpen,
  onClose,
  materialToEdit,
  onSuccess,
}: MaterialFormProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [units, setUnits] = useState<string[]>([]);

  // Carbon factors
  const [carbonUnitValue, setCarbonUnitValue] = useState<number | undefined>(
    undefined
  );
  const [carbonUnitSourceRef, setCarbonUnitSourceRef] = useState<string>("");
  const [carbonKgValue, setCarbonKgValue] = useState<number | undefined>(undefined);
  const [carbonKgSourceRef, setCarbonKgSourceRef] = useState<string>("");
  const [carbonLValue, setCarbonLValue] = useState<number | undefined>(undefined);
  const [carbonLSourceRef, setCarbonLSourceRef] = useState<string>("");
  const [carbonMValue, setCarbonMValue] = useState<number | undefined>(undefined);
  const [carbonMSourceRef, setCarbonMSourceRef] = useState<string>("");

  // Hydric factors
  const [hydricUnitValue, setHydricUnitValue] = useState<number | undefined>(undefined);
  const [hydricUnitSourceRef, setHydricUnitSourceRef] = useState<string>("");
  const [hydricKgValue, setHydricKgValue] = useState<number | undefined>(undefined);
  const [hydricKgSourceRef, setHydricKgSourceRef] = useState<string>("");
  const [hydricLValue, setHydricLValue] = useState<number | undefined>(undefined);
  const [hydricLSourceRef, setHydricLSourceRef] = useState<string>("");
  const [hydricMValue, setHydricMValue] = useState<number | undefined>(undefined);
  const [hydricMSourceRef, setHydricMSourceRef] = useState<string>("");

  // Validation state
  const [nameError, setNameError] = useState("");
  const [unitsError, setUnitsError] = useState("");
  const [factorsError, setFactorsError] = useState("");

  // Fetch information sources for the dropdown
  const { data: infoSources, loading: loadingInfoSources } = useData<InformationSource>({
    collectionName: "setup_infoSources",
  });

  // Fetch existing materials for duplicate validation
  const { data: existingMaterials } = useData<Material>({
    collectionName: "setup_materials",
  });

  // Available unit options
  const unitOptions = [
    { label: t("materials.carbonFactors.unit"), value: "unit" },
    { label: t("materials.carbonFactors.kg"), value: "kg" },
    { label: t("materials.carbonFactors.m"), value: "m" },
    { label: t("materials.carbonFactors.L"), value: "L" },
  ];

  const toggleUnit = (unitValue: string) => {
    setUnits((prevUnits) => {
      if (prevUnits.includes(unitValue)) {
        return prevUnits.filter((unit) => unit !== unitValue);
      } else {
        return [...prevUnits, unitValue];
      }
    });
    // Clear units error when user makes a selection
    if (unitsError) {
      setUnitsError("");
    }
    // Clear factors error when units change
    if (factorsError) {
      setFactorsError("");
    }
  };

  // Check for normalized duplicate names
  const checkForDuplicateName = (inputName: string): boolean => {
    if (!inputName.trim() || !existingMaterials) return false;

    const normalizedInputName = normalizeText(inputName);

    return existingMaterials.some((material) => {
      // Skip the current material being edited
      if (materialToEdit && material.id === materialToEdit.id) {
        return false;
      }

      return normalizeText(material.name) === normalizedInputName;
    });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputName = e.target.value;
    setName(inputName);

    // Clear previous name error
    setNameError("");

    // Check for duplicates
    if (inputName.trim() && checkForDuplicateName(inputName)) {
      setNameError(t("materials.errors.duplicateMaterial"));
    }
  };

  // Check if form has any validation errors
  const hasValidationErrors = (): boolean => {
    return !!(nameError || unitsError || factorsError);
  };

  // Check if form is ready to be submitted
  const isFormValid = (): boolean => {
    return !hasValidationErrors() && name.trim() !== "" && units.length > 0 && !isLoading;
  };

  useEffect(() => {
    if (materialToEdit) {
      setName(materialToEdit.name || "");
      setUnits(materialToEdit.units || []);

      // Set carbon factors
      if (materialToEdit.carbon_factors?.unit) {
        setCarbonUnitValue(materialToEdit.carbon_factors.unit.value);
        setCarbonUnitSourceRef(materialToEdit.carbon_factors.unit.sourceId);
      }
      if (materialToEdit.carbon_factors?.kg) {
        setCarbonKgValue(materialToEdit.carbon_factors.kg.value);
        setCarbonKgSourceRef(materialToEdit.carbon_factors.kg.sourceId);
      }
      if (materialToEdit.carbon_factors?.L) {
        setCarbonLValue(materialToEdit.carbon_factors.L.value);
        setCarbonLSourceRef(materialToEdit.carbon_factors.L.sourceId);
      }
      if (materialToEdit.carbon_factors?.m) {
        setCarbonMValue(materialToEdit.carbon_factors.m.value);
        setCarbonMSourceRef(materialToEdit.carbon_factors.m.sourceId);
      }

      // Set hydric factors
      if (materialToEdit.hydric_factors?.unit) {
        setHydricUnitValue(materialToEdit.hydric_factors.unit.value);
        setHydricUnitSourceRef(materialToEdit.hydric_factors.unit.sourceId);
      }
      if (materialToEdit.hydric_factors?.kg) {
        setHydricKgValue(materialToEdit.hydric_factors.kg.value);
        setHydricKgSourceRef(materialToEdit.hydric_factors.kg.sourceId);
      }
      if (materialToEdit.hydric_factors?.L) {
        setHydricLValue(materialToEdit.hydric_factors.L.value);
        setHydricLSourceRef(materialToEdit.hydric_factors.L.sourceId);
      }
      if (materialToEdit.hydric_factors?.m) {
        setHydricMValue(materialToEdit.hydric_factors.m.value);
        setHydricMSourceRef(materialToEdit.hydric_factors.m.sourceId);
      }
    } else {
      resetForm();
    }
  }, [materialToEdit, isOpen]);

  const resetForm = () => {
    setName("");
    setUnits([]);

    // Reset carbon factors
    setCarbonUnitValue(undefined);
    setCarbonUnitSourceRef("");
    setCarbonKgValue(undefined);
    setCarbonKgSourceRef("");
    setCarbonLValue(undefined);
    setCarbonLSourceRef("");
    setCarbonMValue(undefined);
    setCarbonMSourceRef("");

    // Reset hydric factors
    setHydricUnitValue(undefined);
    setHydricUnitSourceRef("");
    setHydricKgValue(undefined);
    setHydricKgSourceRef("");
    setHydricLValue(undefined);
    setHydricLSourceRef("");
    setHydricMValue(undefined);
    setHydricMSourceRef("");

    // Reset validation
    setNameError("");
    setUnitsError("");
    setFactorsError("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleNumberInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<number | undefined>>
  ) => {
    const value = e.target.value;
    setter(value === "" ? undefined : parseFloat(value));
    // Clear factors error when user inputs values
    if (factorsError) {
      setFactorsError("");
    }
  };

  const handleSourceChange = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    setter(value);
    // Clear factors error when user selects sources
    if (factorsError) {
      setFactorsError("");
    }
  };

  const validateForm = () => {
    let isValid = true;

    // Validate name
    if (!name.trim()) {
      setNameError(t("materials.errors.nameRequired"));
      isValid = false;
    } else if (checkForDuplicateName(name)) {
      setNameError(t("materials.errors.duplicateMaterial"));
      isValid = false;
    } else {
      setNameError("");
    }

    // Validate units
    if (units.length === 0) {
      setUnitsError(t("materials.errors.unitsRequired"));
      isValid = false;
    } else {
      setUnitsError("");
    }

    // Validate factors
    if (units.length > 0) {
      let hasAnyFactor = false;

      // Check if any selected unit has at least one factor (carbon or hydric)
      for (const unit of units) {
        let hasFactorForUnit = false;

        switch (unit) {
          case "unit":
            hasFactorForUnit =
              (carbonUnitValue !== undefined && carbonUnitSourceRef !== "") ||
              (hydricUnitValue !== undefined && hydricUnitSourceRef !== "");
            break;
          case "kg":
            hasFactorForUnit =
              (carbonKgValue !== undefined && carbonKgSourceRef !== "") ||
              (hydricKgValue !== undefined && hydricKgSourceRef !== "");
            break;
          case "L":
            hasFactorForUnit =
              (carbonLValue !== undefined && carbonLSourceRef !== "") ||
              (hydricLValue !== undefined && hydricLSourceRef !== "");
            break;
          case "m":
            hasFactorForUnit =
              (carbonMValue !== undefined && carbonMSourceRef !== "") ||
              (hydricMValue !== undefined && hydricMSourceRef !== "");
            break;
        }

        if (hasFactorForUnit) {
          hasAnyFactor = true;
          break;
        }
      }

      if (!hasAnyFactor) {
        setFactorsError(t("materials.errors.factorsRequired"));
        isValid = false;
      } else {
        setFactorsError("");
      }
    }

    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!user?.uid) {
      toast({
        title: t("errors.unauthorized.title"),
        description: t("errors.unauthorized.description"),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const carbon_factors: Material["carbon_factors"] = {};
      const hydric_factors: Material["hydric_factors"] = {};

      // Add carbon factors if units are selected
      if (
        units.includes("unit") &&
        carbonUnitValue !== undefined &&
        carbonUnitSourceRef
      ) {
        carbon_factors.unit = {
          value: carbonUnitValue,
          sourceId: carbonUnitSourceRef,
        };
      }
      if (units.includes("kg") && carbonKgValue !== undefined && carbonKgSourceRef) {
        carbon_factors.kg = { value: carbonKgValue, sourceId: carbonKgSourceRef };
      }
      if (
        units.includes("L") &&
        carbonLValue !== undefined &&
        carbonLSourceRef
      ) {
        carbon_factors.L = { value: carbonLValue, sourceId: carbonLSourceRef };
      }
      if (units.includes("m") && carbonMValue !== undefined && carbonMSourceRef) {
        carbon_factors.m = {
          value: carbonMValue,
          sourceId: carbonMSourceRef,
        };
      }

      // Add hydric factors if units are selected
      if (
        units.includes("unit") &&
        hydricUnitValue !== undefined &&
        hydricUnitSourceRef
      ) {
        hydric_factors.unit = {
          value: hydricUnitValue,
          sourceId: hydricUnitSourceRef,
        };
      }
      if (units.includes("kg") && hydricKgValue !== undefined && hydricKgSourceRef) {
        hydric_factors.kg = { value: hydricKgValue, sourceId: hydricKgSourceRef };
      }
      if (
        units.includes("L") &&
        hydricLValue !== undefined &&
        hydricLSourceRef
      ) {
        hydric_factors.L = { value: hydricLValue, sourceId: hydricLSourceRef };
      }
      if (units.includes("m") && hydricMValue !== undefined && hydricMSourceRef) {
        hydric_factors.m = {
          value: hydricMValue,
          sourceId: hydricMSourceRef,
        };
      }

      const materialData = {
        name,
        units,
        carbon_factors: Object.keys(carbon_factors).length > 0 ? carbon_factors : undefined,
        hydric_factors: Object.keys(hydric_factors).length > 0 ? hydric_factors : undefined,
      };

      if (materialToEdit?.id) {
        // Edit existing material
        await materialService.edit(materialToEdit.id, materialData, user.uid);

        toast({
          title: t("materials.updated"),
          description: `${name} ${t(
            "materials.toasts.updateSuccess.description"
          )}`,
        });
      } else {
        // Create new material
        await materialService.create(materialData, user.uid);

        toast({
          title: t("materials.created"),
          description: `${name} ${t(
            "materials.toasts.createSuccess.description"
          )}`,
        });
      }

      onSuccess();
      handleClose();
    } catch (error) {
      console.error("Error saving material:", error);
      
      // Handle specific API errors
      let errorMessage = (error as Error).message;
      if (errorMessage === "MATERIAL_DUPLICATE") {
        errorMessage = t("materials.errors.duplicateMaterial");
      }
      
      toast({
        title: materialToEdit
          ? t("materials.errors.updateError")
          : t("materials.errors.createError"),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter out archived info sources
  const activeInfoSources = infoSources
    ? infoSources.filter((source) => !source.isArchived)
    : [];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {materialToEdit
              ? t("materials.editTitle")
              : t("materials.addTitle")}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Material name */}
          <div className="grid gap-2">
            <Label htmlFor="name" className="required">
              {t("materials.name")} *
            </Label>
            <Input
              id="name"
              placeholder={t("materials.namePlaceholder")}
              value={name}
              onChange={handleNameChange}
              className={nameError ? "border-red-500" : ""}
              disabled={isLoading}
            />
            {nameError && <p className="text-red-500 text-sm">{nameError}</p>}
          </div>

          {/* Units selection */}
          <div className="grid gap-2">
            <Label className="required">{t("materials.factors")} *</Label>
            <div className="flex flex-wrap gap-4">
              {unitOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`unit-${option.value}`}
                    checked={units.includes(option.value)}
                    onCheckedChange={() => toggleUnit(option.value)}
                    disabled={isLoading}
                  />
                  <Label
                    htmlFor={`unit-${option.value}`}
                    className="cursor-pointer"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
            {unitsError && <p className="text-red-500 text-sm">{unitsError}</p>}
          </div>

          {/* Carbon factors section */}
          {units.length > 0 && (
            <fieldset className="space-y-3 pt-3 border-t border-gray-200">
              <legend className="font-medium text-sm px-2 -ml-2">{t("materials.carbonFactors.title")} (kg CO2e)</legend>

              {/* Unit carbon factor */}
              {units.includes("unit") && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="carbonUnitValue">
                      {t("materials.carbonFactors.unit")}
                    </Label>
                    <Input
                      id="carbonUnitValue"
                      type="number"
                      step="any"
                      min="0"
                      placeholder={t("materials.carbonFactors.unitPlaceholder")}
                      value={
                        carbonUnitValue === undefined ? "" : carbonUnitValue
                      }
                      onChange={(e) =>
                        handleNumberInputChange(e, setCarbonUnitValue)
                      }
                      disabled={isLoading}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="carbonUnitSource">
                      {t("materials.source")}
                    </Label>
                    <Select
                      value={carbonUnitSourceRef}
                      onValueChange={(value) =>
                        handleSourceChange(value, setCarbonUnitSourceRef)
                      }
                      disabled={
                        isLoading ||
                        loadingInfoSources ||
                        carbonUnitValue === undefined
                      }
                    >
                      <SelectTrigger id="carbonUnitSource" className="w-full">
                        <SelectValue
                          placeholder={t("materials.sourcePlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {activeInfoSources.map((source) => (
                          <SelectItem key={source.id} value={source.id}>
                            {source.name} ({source.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Ton carbon factor */}
              {units.includes("kg") && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="carbonKgValue">{t("materials.carbonFactors.kg")}</Label>
                    <Input
                      id="carbonKgValue"
                      type="number"
                      step="any"
                      min="0"
                      placeholder={t("materials.carbonFactors.kgPlaceholder")}
                      value={carbonKgValue === undefined ? "" : carbonKgValue}
                      onChange={(e) =>
                        handleNumberInputChange(e, setCarbonKgValue)
                      }
                      disabled={isLoading}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="carbonKgSource">{t("materials.source")}</Label>
                    <Select
                      value={carbonKgSourceRef}
                      onValueChange={(value) => handleSourceChange(value, setCarbonKgSourceRef)}
                      disabled={isLoading || loadingInfoSources || carbonKgValue === undefined}
                    >
                      <SelectTrigger id="carbonKgSource" className="w-full">
                        <SelectValue placeholder={t("materials.sourcePlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {activeInfoSources.map((source) => (
                          <SelectItem key={source.id} value={source.id}>
                            {source.name} ({source.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Liter carbon factor */}
              {units.includes("L") && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="carbonLValue">
                      {t("materials.carbonFactors.L")}
                    </Label>
                    <Input
                      id="carbonLValue"
                      type="number"
                      step="any"
                      min="0"
                      placeholder={t("materials.carbonFactors.LPlaceholder")}
                      value={carbonLValue === undefined ? "" : carbonLValue}
                      onChange={(e) =>
                        handleNumberInputChange(e, setCarbonLValue)
                      }
                      disabled={isLoading}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="carbonLSource">
                      {t("materials.source")}
                    </Label>
                    <Select
                      value={carbonLSourceRef}
                      onValueChange={(value) =>
                        handleSourceChange(value, setCarbonLSourceRef)
                      }
                      disabled={
                        isLoading ||
                        loadingInfoSources ||
                        carbonLValue === undefined
                      }
                    >
                      <SelectTrigger id="carbonLSource" className="w-full">
                        <SelectValue
                          placeholder={t("materials.sourcePlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {activeInfoSources.map((source) => (
                          <SelectItem key={source.id} value={source.id}>
                            {source.name} ({source.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Euro carbon factor */}
              {units.includes("m") && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="carbonMValue">{t("materials.carbonFactors.m")}</Label>
                    <Input
                      id="carbonMValue"
                      type="number"
                      step="any"
                      min="0"
                      placeholder={t("materials.carbonFactors.mPlaceholder")}
                      value={carbonMValue === undefined ? "" : carbonMValue}
                      onChange={(e) =>
                        handleNumberInputChange(e, setCarbonMValue)
                      }
                      disabled={isLoading}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="carbonMSource">{t("materials.source")}</Label>
                    <Select
                      value={carbonMSourceRef}
                      onValueChange={(value) => handleSourceChange(value, setCarbonMSourceRef)}
                      disabled={isLoading || loadingInfoSources || carbonMValue === undefined}
                    >
                      <SelectTrigger id="carbonMSource" className="w-full">
                        <SelectValue placeholder={t("materials.sourcePlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {activeInfoSources.map((source) => (
                          <SelectItem key={source.id} value={source.id}>
                            {source.name} ({source.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </fieldset>
          )}

          {/* Hydric factors section */}
          {units.length > 0 && (
            <fieldset className="space-y-3 pt-3 border-t border-gray-200">
              <legend className="font-medium text-sm px-2 -ml-2">
                {t("materials.hydricFactors.title")}
              </legend>

              {/* Unit hydric factor */}
              {units.includes("unit") && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="hydricUnitValue">
                      {t("materials.hydricFactors.unit")}
                    </Label>
                    <Input
                      id="hydricUnitValue"
                      type="number"
                      step="any"
                      min="0"
                      placeholder={t("materials.hydricFactors.unitPlaceholder")}
                      value={
                        hydricUnitValue === undefined ? "" : hydricUnitValue
                      }
                      onChange={(e) =>
                        handleNumberInputChange(e, setHydricUnitValue)
                      }
                      disabled={isLoading}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="hydricUnitSource">
                      {t("materials.source")}
                    </Label>
                    <Select
                      value={hydricUnitSourceRef}
                      onValueChange={(value) =>
                        handleSourceChange(value, setHydricUnitSourceRef)
                      }
                      disabled={
                        isLoading ||
                        loadingInfoSources ||
                        hydricUnitValue === undefined
                      }
                    >
                      <SelectTrigger id="hydricUnitSource" className="w-full">
                        <SelectValue
                          placeholder={t("materials.sourcePlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {activeInfoSources.map((source) => (
                          <SelectItem key={source.id} value={source.id}>
                            {source.name} ({source.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Ton hydric factor */}
              {units.includes("kg") && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="hydricKgValue">{t("materials.hydricFactors.kg")}</Label>
                    <Input
                      id="hydricKgValue"
                      type="number"
                      step="any"
                      min="0"
                      placeholder={t("materials.hydricFactors.kgPlaceholder")}
                      value={hydricKgValue === undefined ? "" : hydricKgValue}
                      onChange={(e) =>
                        handleNumberInputChange(e, setHydricKgValue)
                      }
                      disabled={isLoading}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="hydricKgSource">{t("materials.source")}</Label>
                    <Select
                      value={hydricKgSourceRef}
                      onValueChange={(value) => handleSourceChange(value, setHydricKgSourceRef)}
                      disabled={isLoading || loadingInfoSources || hydricKgValue === undefined}
                    >
                      <SelectTrigger id="hydricKgSource" className="w-full">
                        <SelectValue placeholder={t("materials.sourcePlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {activeInfoSources.map((source) => (
                          <SelectItem key={source.id} value={source.id}>
                            {source.name} ({source.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Liter hydric factor */}
              {units.includes("L") && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="hydricLValue">
                      {t("materials.hydricFactors.L")}
                    </Label>
                    <Input
                      id="hydricLValue"
                      type="number"
                      step="any"
                      min="0"
                      placeholder={t("materials.hydricFactors.LPlaceholder")}
                      value={hydricLValue === undefined ? "" : hydricLValue}
                      onChange={(e) =>
                        handleNumberInputChange(e, setHydricLValue)
                      }
                      disabled={isLoading}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="hydricLSource">
                      {t("materials.source")}
                    </Label>
                    <Select
                      value={hydricLSourceRef}
                      onValueChange={(value) =>
                        handleSourceChange(value, setHydricLSourceRef)
                      }
                      disabled={
                        isLoading ||
                        loadingInfoSources ||
                        hydricLValue === undefined
                      }
                    >
                      <SelectTrigger id="hydricLSource" className="w-full">
                        <SelectValue
                          placeholder={t("materials.sourcePlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {activeInfoSources.map((source) => (
                          <SelectItem key={source.id} value={source.id}>
                            {source.name} ({source.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Euro hydric factor */}
              {units.includes("m") && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="hydricMValue">{t("materials.hydricFactors.m")}</Label>
                    <Input
                      id="hydricMValue"
                      type="number"
                      step="any"
                      min="0"
                      placeholder={t("materials.hydricFactors.mPlaceholder")}
                      value={hydricMValue === undefined ? "" : hydricMValue}
                      onChange={(e) =>
                        handleNumberInputChange(e, setHydricMValue)
                      }
                      disabled={isLoading}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="hydricMSource">{t("materials.source")}</Label>
                    <Select
                      value={hydricMSourceRef}
                      onValueChange={(value) => handleSourceChange(value, setHydricMSourceRef)}
                      disabled={isLoading || loadingInfoSources || hydricMValue === undefined}
                    >
                      <SelectTrigger id="hydricMSource" className="w-full">
                        <SelectValue placeholder={t("materials.sourcePlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {activeInfoSources.map((source) => (
                          <SelectItem key={source.id} value={source.id}>
                            {source.name} ({source.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </fieldset>
          )}

          {/* Display factors error if any */}
          {factorsError && (
            <p className="text-red-500 text-sm">{factorsError}</p>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              {t("materials.cancel")}
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={handleSubmit}
                    disabled={!isFormValid()}
                    className={`${
                      !isFormValid()
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-teal-600 text-white hover:bg-teal-700"
                    }`}
                  >
                    {isLoading ? t("materials.saving") : t("materials.save")}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{!isFormValid() ? t("materials.saveTooltip") : t("materials.save")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
